import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Switch,
  Chip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@material-ui/core';
import { format } from 'date-fns';

import { ApplicationState } from '../../../store';
import { fetchUserDetailRequest } from '../../../store/modules/admin/actions';
import { Container, useStyles } from './UserDetailStyles';
import api from '../../../services/api';
import { toast } from 'react-toastify';

export default function UserDetailView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { userDetail, loading } = useSelector((state: ApplicationState) => state.admin);
  const { user, payments } = userDetail;

  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    referenceMonth: format(new Date(), 'yyyy-MM'),
    status: 'paid',
    notes: '',
  });

  useEffect(() => {
    dispatch(fetchUserDetailRequest(id));
  }, [dispatch, id]);

  async function handleToggleActive() {
    try {
      await api.patch(`/admin/users/${id}/toggleActive`);
      dispatch(fetchUserDetailRequest(id));
    } catch {
      toast.error('Erro ao alterar status.');
    }
  }

  async function handleCreatePayment() {
    try {
      await api.post('/admin/payments', {
        userId: id,
        amount: parseFloat(paymentForm.amount) || 0,
        referenceMonth: paymentForm.referenceMonth,
        status: paymentForm.status,
        notes: paymentForm.notes,
      });
      toast.success('Pagamento registrado!');
      setPaymentDialog(false);
      setPaymentForm({ amount: '', referenceMonth: format(new Date(), 'yyyy-MM'), status: 'paid', notes: '' });
      dispatch(fetchUserDetailRequest(id));
    } catch {
      toast.error('Erro ao registrar pagamento.');
    }
  }

  function getStatusChip(status: string) {
    if (status === 'paid') return <Chip label="Pago" size="small" className={classes.chipPaid} />;
    if (status === 'pending') return <Chip label="Pendente" size="small" className={classes.chipPending} />;
    return <Chip label="Atrasado" size="small" className={classes.chipOverdue} />;
  }

  function getSourceChip(source: string) {
    if (source === 'kiwify') return <Chip label="Kiwify" size="small" className={classes.chipKiwify} />;
    return <Chip label="Manual" size="small" className={classes.chipManual} />;
  }

  if (loading || !user) {
    return <CircularProgress style={{ margin: '40px auto', display: 'block' }} />;
  }

  return (
    <Container>
      <div className={classes.infoCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography className={classes.label}>Nome</Typography>
            <Typography className={classes.value}>{user.basicInfo.name}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography className={classes.label}>Email</Typography>
            <Typography className={classes.value}>{user.email}</Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography className={classes.label}>Telefone</Typography>
            <Typography className={classes.value}>{user.basicInfo.phone}</Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography className={classes.label}>Criado em</Typography>
            <Typography className={classes.value}>
              {format(new Date(user.createdAt), 'dd/MM/yyyy')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography className={classes.label}>Ativo</Typography>
            <Switch
              checked={user.isActive}
              onChange={handleToggleActive}
              color="primary"
            />
          </Grid>
        </Grid>
      </div>

      <div className={classes.tableContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <Typography className={classes.sectionTitle}>Historico de Pagamentos</Typography>
          <Button variant="contained" color="primary" size="small" onClick={() => setPaymentDialog(true)}>
            Registrar Pagamento
          </Button>
        </div>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Valor</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Mes Ref.</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Notas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map(payment => (
              <TableRow key={payment._id}>
                <TableCell>R$ {payment.amount.toFixed(2)}</TableCell>
                <TableCell>{format(new Date(payment.date), 'dd/MM/yyyy')}</TableCell>
                <TableCell>{payment.referenceMonth}</TableCell>
                <TableCell>{getStatusChip(payment.status)}</TableCell>
                <TableCell>{getSourceChip(payment.source)}</TableCell>
                <TableCell>{payment.notes || '-'}</TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum pagamento encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)}>
        <DialogTitle>Registrar Pagamento</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '350px' }}>
            <TextField
              label="Valor"
              variant="outlined"
              size="small"
              type="number"
              value={paymentForm.amount}
              onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            />
            <TextField
              label="Mes Referencia (yyyy-MM)"
              variant="outlined"
              size="small"
              value={paymentForm.referenceMonth}
              onChange={e => setPaymentForm({ ...paymentForm, referenceMonth: e.target.value })}
            />
            <TextField
              label="Notas"
              variant="outlined"
              size="small"
              multiline
              rows={2}
              value={paymentForm.notes}
              onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreatePayment}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
